// src/components/VoucherModal.js
import React, { useState, useEffect } from "react";
import "../styles/voucherModal.css";

/* =====================================================
   Configuration
===================================================== */

// Always call backend explicitly
const API_BASE =
  process.env.REACT_APP_API_BASE || "http://localhost:5000";

// DEV detection (shared rule across app)
const isDevEmail = (email) =>
  typeof email === "string" && email.endsWith("@dev.edgeneweb");

/* =====================================================
   Component
===================================================== */
const VoucherModal = ({
  isOpen,
  onClose,
  plan,
  onVoucherUsed,
  userEmail,
  onContinueToPayment,
}) => {
  const [voucherCode, setVoucherCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [remainingVouchers, setRemainingVouchers] = useState(null);

  /* =====================================================
     Fetch remaining vouchers (SAFE)
  ===================================================== */
  useEffect(() => {
    if (!isOpen) return;

    setVoucherCode("");
    setError("");
    setRemainingVouchers(null);

    // DEV shortcut — backend not required
    if (isDevEmail(userEmail)) {
      setRemainingVouchers(999);
      return;
    }

    const fetchVouchersStatus = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/vouchers/remaining`
        );

        if (!res.ok) {
          throw new Error(`HTTP ${res.status}`);
        }

        const text = await res.text();

        // Defensive parsing
        if (!text.startsWith("{")) {
          throw new Error(
            "Invalid response (expected JSON)"
          );
        }

        const data = JSON.parse(text);
        setRemainingVouchers(data.remaining ?? 0);
      } catch (err) {
        console.error(
          "Error fetching remaining voucher count:",
          err
        );
        setError("Failed to load voucher count.");
      }
    };

    fetchVouchersStatus();
  }, [isOpen, userEmail]);

  /* =====================================================
     Apply Voucher
  ===================================================== */
  const handleVoucherSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!voucherCode.trim()) {
      setError("Please enter a voucher code.");
      return;
    }

    if (!userEmail) {
      setError("User email missing.");
      return;
    }

    setIsLoading(true);

    // DEV shortcut — no backend call
    if (isDevEmail(userEmail)) {
      onVoucherUsed(voucherCode.trim());
      onClose();
      setIsLoading(false);
      return;
    }

    try {
      const res = await fetch(
        `${API_BASE}/api/vouchers/verify`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            voucherCode: voucherCode.trim(),
            email: userEmail,
            planId: plan.id,
          }),
        }
      );

      if (!res.ok) {
        const err = await res.json();
        throw new Error(
          err.message || "Voucher invalid"
        );
      }

      const data = await res.json();
      console.log("Voucher applied:", data);

      onVoucherUsed(voucherCode.trim());
      onClose();
    } catch (err) {
      console.error("Voucher verification error:", err);
      setError(
        err.message ||
          "An error occurred while applying voucher."
      );
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen || !plan) return null;

  /* =====================================================
     Render
  ===================================================== */
  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2 className="modal-title">
            Apply Your Voucher
          </h2>
          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close modal"
          >
            &times;
          </button>
        </div>

        <div className="modal-body">
          <p className="modal-message">
            You may be eligible for a free month on the{" "}
            <strong>{plan.title}</strong> plan.
            {remainingVouchers !== null && (
              <>
                {" "}
                There are currently{" "}
                <strong>{remainingVouchers}</strong>{" "}
                vouchers remaining.
              </>
            )}
          </p>

          <form
            className="voucher-form"
            onSubmit={handleVoucherSubmit}
          >
            <input
              type="text"
              placeholder="Enter voucher code"
              value={voucherCode}
              onChange={(e) =>
                setVoucherCode(e.target.value)
              }
              disabled={isLoading}
            />

            {error && (
              <p className="modal-error">{error}</p>
            )}

            <button
              type="submit"
              className="modal-button"
              disabled={
                isLoading || !voucherCode.trim()
              }
            >
              {isLoading
                ? "Applying..."
                : "Apply Voucher"}
            </button>
          </form>

          <div className="modal-footer">
            <button
              className="modal-button secondary-button"
              onClick={onContinueToPayment}
              disabled={isLoading}
            >
              Continue to Payment
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VoucherModal;
